/**
 * 手入力画面 (v0.2 パターンB)。人件費・諸経費・デリバリー売上の 3 種を
 * 単一の汎用コンポーネント (ManualEntryPage) + データ種別ごとの config で実装。
 *
 * 設計方針 (docs/v0.2-outline.md §1 準拠):
 *   - 店舗・対象月を画面上部で 1 度選択 → 全行に自動反映
 *   - 既存データを自動読込 (GET /data/records) → 編集/削除も同一画面で可能
 *   - 保存は既存の CSV 取込 API (POST /imports/data/{type} → commit) を再利用
 *     (labor/opex/delivery_sales は CLAUDE.md §8-9 と同様「店舗×月の全置換」方式のため、
 *      既存データを読み込んだ状態で編集・追加・削除してから保存する UX が自然に成立する)
 *   - 新規バックエンド API は追加していない (既存 preview/commit/records エンドポイントを流用)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, AlertTriangle, Info, Wallet, Receipt, Bike } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized } from '@/lib/api';
import { buildCsvText } from '@/lib/csv-export';
import { toast } from '@/stores/toast';
import { ConfirmDialog } from '@/components/Modal';
import { MonthPicker } from '@/components/MonthPicker';
import { InputReadyButton } from '@/components/publish/InputReadyButton';

type Store = { store_id: string; store_code: string; store_name: string; status: 'active' | 'closed' };
type DeliveryService = { delivery_service_id: string; store_id: string; service_code: string; service_name: string; active_flg: number };
type RecordsResponse = {
  data_type: string;
  store_id: string | null;
  month: string | null;
  count: number;
  rows: Array<Record<string, unknown>>;
  csv_headers: string[];
  csv_filename: string;
};
type ImportError = { row: number; code?: string; column?: string; message: string };
type PreviewResponse = {
  import_job_id: string;
  invalid_rows: number;
  errors: ImportError[];
};
type CommitResponse = { inserted: number; error_count: number; errors: ImportError[] };

type FieldConfig = {
  /** CSV 列名 (backend の rowSchema と一致させる) */
  name: string;
  label: string;
  type: 'number' | 'text' | 'select';
  required: boolean;
  placeholder?: string;
  selectOptions?: Array<{ value: string; label: string }>;
  /** 'delivery_services' 指定時、選択済み店舗の delivery_service_master から動的に選択肢を作る */
  dynamicSelect?: 'delivery_services';
  /** true の場合、負数入力時にセル枠を警告色にし、保存確認ダイアログで件数を明示する。
   *  返金・値引き調整等で負数を意図的に使う諸経費/デリバリー売上向け (人件費は backend の
   *  nonnegative 制約で負数自体を弾くため未設定)。負数と正数は別レコードとして保存され、
   *  ダッシュボード側の SUM() 集計で自動的にネットされる (マージ処理は行わない) */
  warnIfNegative?: boolean;
};

export type ManualEntryConfig = {
  dataType: 'labor' | 'opex' | 'delivery_sales';
  title: string;
  description: string;
  icon: LucideIcon;
  monthColumn: 'target_month' | 'sales_month';
  /** singleRow=true: 店舗×月で1件のみ (人件費)。false: 複数行入力可 (諸経費・デリバリー売上) */
  singleRow: boolean;
  fields: FieldConfig[];
};

const OPEX_ITEM_TYPES = [
  { value: 'rent', label: '賃料' },
  { value: 'electricity', label: '電気' },
  { value: 'gas', label: 'ガス' },
  { value: 'water', label: '水道' },
  { value: 'telephone', label: '通信' },
  { value: 'insurance', label: '保険' },
  { value: 'other', label: 'その他' },
];

const TAX_RATE_OPTIONS = [
  { value: '0.10', label: '10%（標準）' },
  { value: '0.08', label: '8%（軽減）' },
];

/** 既存データの tax_rate (0.1 / 0.08 等の数値) を選択肢の文字列表現に正規化 */
const normalizeTaxRate = (v: unknown): string => {
  const n = Number(v);
  if (Number.isNaN(n)) return '0.10';
  const match = TAX_RATE_OPTIONS.find((o) => Math.abs(Number(o.value) - n) < 0.001);
  return match?.value ?? n.toFixed(2);
};

export const LABOR_CONFIG: ManualEntryConfig = {
  dataType: 'labor',
  title: '人件費 手入力',
  description: '店舗・対象月ごとの人件費（税抜）を入力します。既に登録済みの場合は自動で読み込まれます',
  icon: Wallet,
  monthColumn: 'target_month',
  singleRow: true,
  fields: [
    { name: 'labor_cost_amount', label: '人件費（税抜、円）', type: 'number', required: true, placeholder: '2138630' },
  ],
};

export const OPEX_CONFIG: ManualEntryConfig = {
  dataType: 'opex',
  title: '諸経費 手入力',
  description: '店舗・対象月ごとの諸経費（賃料・電気・ガス等）を項目ごとに入力します',
  icon: Receipt,
  monthColumn: 'target_month',
  singleRow: false,
  fields: [
    { name: 'item_type', label: '費目種別', type: 'select', required: true, selectOptions: OPEX_ITEM_TYPES },
    { name: 'item_name', label: '費目名（任意）', type: 'text', required: false, placeholder: '例: 賃料' },
    { name: 'amount', label: '金額（税抜、円）', type: 'number', required: true, placeholder: '577500', warnIfNegative: true },
    { name: 'tax_rate', label: '税率', type: 'select', required: false, selectOptions: TAX_RATE_OPTIONS },
  ],
};

export const DELIVERY_SALES_CONFIG: ManualEntryConfig = {
  dataType: 'delivery_sales',
  title: 'デリバリー売上 手入力',
  description: '店舗・対象月ごとのデリバリーサービス別売上（税込）を入力します',
  icon: Bike,
  monthColumn: 'sales_month',
  singleRow: false,
  fields: [
    { name: 'service_code', label: 'デリバリー業者', type: 'select', required: true, dynamicSelect: 'delivery_services' },
    { name: 'amount', label: '金額（税込、円）', type: 'number', required: true, placeholder: '250315', warnIfNegative: true },
    { name: 'tax_rate', label: '税率', type: 'select', required: false, selectOptions: TAX_RATE_OPTIONS },
  ],
};

const currentYearMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const emptyRow = (fields: FieldConfig[]): Record<string, string> =>
  Object.fromEntries(fields.map((f) => [f.name, f.name === 'tax_rate' ? '0.10' : '']));

export function ManualEntryPage({ config }: { config: ManualEntryConfig }) {
  const qc = useQueryClient();
  const Icon = config.icon;

  const [storeId, setStoreId] = useState('');
  const [month, setMonth] = useState(currentYearMonth());
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [errors, setErrors] = useState<ImportError[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // 直近に「既存データから読み込んだ」store+month の組を記録し、それ以外のタイミングで
  // rows を勝手に上書きしないようにする (ユーザーの入力中の編集を保護)
  const loadedKeyRef = useRef<string | null>(null);

  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });

  const hasDynamicServiceField = config.fields.some((f) => f.dynamicSelect === 'delivery_services');
  const { data: deliveryServices = [] } = useQuery<DeliveryService[]>({
    queryKey: ['delivery-services'],
    queryFn: () => apiFetch<DeliveryService[]>('/delivery-services'),
    enabled: hasDynamicServiceField,
  });

  const existingQuery = useQuery<RecordsResponse>({
    queryKey: ['data-records', config.dataType, storeId, month],
    queryFn: () => {
      const params = new URLSearchParams({ data_type: config.dataType, store_id: storeId, month });
      return apiFetch<RecordsResponse>(`/data/records?${params}`);
    },
    enabled: Boolean(storeId && month),
  });

  // 店舗×月が変わって新しい既存データが読み込まれたら rows を初期化
  useEffect(() => {
    if (!storeId || !month || !existingQuery.data) return;
    const key = `${storeId}::${month}`;
    if (loadedKeyRef.current === key) return; // 既にこの組み合わせで読込済み (再フェッチによる上書き防止)
    loadedKeyRef.current = key;

    const existingRows = existingQuery.data.rows.map((r) => {
      const row: Record<string, string> = {};
      for (const f of config.fields) {
        const raw = r[f.name];
        row[f.name] = f.name === 'tax_rate' ? normalizeTaxRate(raw) : raw === null || raw === undefined ? '' : String(raw);
      }
      return row;
    });

    if (config.singleRow) {
      setRows(existingRows.length > 0 ? [existingRows[0]!] : [emptyRow(config.fields)]);
    } else {
      setRows(existingRows);
    }
    setErrors(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, month, existingQuery.data]);

  // store/month が未選択に戻ったら rows をクリア
  useEffect(() => {
    if (!storeId || !month) {
      setRows([]);
      loadedKeyRef.current = null;
    }
  }, [storeId, month]);

  const selectedStore = stores.find((s) => s.store_id === storeId);

  const serviceOptionsForStore = useMemo(() => {
    if (!hasDynamicServiceField) return [];
    // v0.2 コードキー化 (2026-07-31): 識別子は service_code、表示名は service_name
    const opts = deliveryServices
      .filter((s) => s.store_id === storeId && s.active_flg === 1)
      .map((s) => ({ value: s.service_code, label: s.service_name }));
    // 既存行に含まれるが選択肢に無い値 (無効化済み・削除済み等) も表示崩れ防止のため追加
    const known = new Set(opts.map((o) => o.value));
    for (const r of rows) {
      const v = r.service_code;
      if (v && !known.has(v)) { opts.push({ value: v, label: `${v}（無効/未登録）` }); known.add(v); }
    }
    return opts;
  }, [deliveryServices, storeId, rows, hasDynamicServiceField]);

  const addRow = () => setRows((prev) => [...prev, emptyRow(config.fields)]);
  const removeRow = (idx: number) => setRows((prev) => prev.filter((_, i) => i !== idx));
  const updateCell = (idx: number, field: string, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx]!, [field]: value };
      return next;
    });
  };

  const isRowFilled = (row: Record<string, string>) =>
    config.fields.some((f) => f.required && (row[f.name] ?? '').trim() !== '');

  const filledRows = rows.filter(isRowFilled);
  // 現在の store+month に対して既存データの読込が完了しているか (レビュー指摘 CRITICAL 対応:
  //   店舗/月を切替えた直後、GET /data/records の再フェッチが完了する前に保存すると、
  //   古い rows の内容が新しい store_code/month で全置換送信されてしまう事故を防ぐ)
  const dataReady = Boolean(storeId && month) && loadedKeyRef.current === `${storeId}::${month}` && !existingQuery.isFetching;

  // 負数許容フィールドに負の値が入っている行数 (返金・値引き等の意図的な入力かを保存前に確認させる)
  const negativeFields = config.fields.filter((f) => f.warnIfNegative);
  const negativeRowCount = negativeFields.length === 0 ? 0 : filledRows.filter((row) =>
    negativeFields.some((f) => { const v = row[f.name]; return v !== undefined && v.trim() !== '' && Number(v) < 0; }),
  ).length;

  const openConfirm = () => {
    if (!storeId || !selectedStore) { toast.error('店舗を選択してください'); return; }
    if (!month) { toast.error('対象月を選択してください'); return; }
    if (!dataReady) { toast.error('既存データの読込が完了していません。少し待ってから再度お試しください'); return; }
    if (filledRows.length === 0) { toast.error('入力内容がありません'); return; }
    setConfirmOpen(true);
  };

  const doSave = async () => {
    if (!storeId || !selectedStore || !month) return;

    const headers = ['store_code', config.monthColumn, ...config.fields.map((f) => f.name)];
    const csvRows = filledRows.map((row) => ({
      store_code: selectedStore.store_code,
      [config.monthColumn]: `${month}-01`,
      ...row,
    }));
    // escapeFormulas=false: このCSVはExcel等を一切介さずブラウザ生成→自前パーサへ即アップロードする
    // 往復専用データのため formula-injection エスケープ (先頭 "-" 等への "'" 前置) は不要かつ有害
    // (負の金額が壊れる、item_name 等の自由記述欄に "'" が永続保存される) — レビュー指摘 HIGH/CRITICAL 対応
    const csvText = buildCsvText(headers, csvRows, false);
    const file = new File([csvText], `manual-${config.dataType}.csv`, { type: 'text/csv' });

    setSaving(true);
    setErrors(null);
    setConfirmOpen(false);
    try {
      const form = new FormData();
      form.append('file', file);
      const token = localStorage.getItem('access_token');
      const previewRes = await fetch(`${API_BASE}/imports/data/${config.dataType}?mode=upsert`, {
        method: 'POST',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const previewBody = await previewRes.json();
      if (!previewRes.ok || !previewBody.success) {
        handleUnauthorized(previewRes.status);
        throw new ApiClientError(previewRes.status, previewBody.error?.code ?? 'UNKNOWN', previewBody.error?.message ?? '検証に失敗しました');
      }
      const preview = previewBody.data as PreviewResponse;
      if (preview.invalid_rows > 0) {
        setErrors(preview.errors);
        toast.error(`入力内容にエラーがあります (${preview.invalid_rows} 件)`);
        return;
      }

      const commit = await apiFetch<CommitResponse>(`/imports/${preview.import_job_id}/commit`, { method: 'POST' });
      if (commit.error_count > 0) {
        setErrors(commit.errors);
        toast.error(`保存中にエラーが発生しました (${commit.error_count} 件)`);
        return;
      }

      toast.success(`保存しました（${commit.inserted} 件）`);
      loadedKeyRef.current = null; // 再読込を許可
      qc.invalidateQueries({ queryKey: ['data-records', config.dataType, storeId, month] });
      qc.invalidateQueries({ queryKey: ['data-summary'] });
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (row: Record<string, string>, idx: number, field: FieldConfig) => {
    const value = row[field.name] ?? '';
    if (field.type === 'select') {
      const options = field.dynamicSelect === 'delivery_services' ? serviceOptionsForStore : field.selectOptions ?? [];
      return (
        <select
          value={value}
          onChange={(e) => updateCell(idx, field.name, e.target.value)}
          className="w-full min-w-[120px] px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring"
        >
          <option value="">選択してください</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    // 負数許容フィールド (返金・値引き等) は視覚的に区別: 枠色を警告色にして意図的な入力であることを促す
    const isNegative = field.warnIfNegative && value.trim() !== '' && Number(value) < 0;
    return (
      <div>
        <input
          type={field.type === 'number' ? 'number' : 'text'}
          value={value}
          onChange={(e) => updateCell(idx, field.name, e.target.value)}
          placeholder={field.placeholder}
          className={`w-full min-w-[100px] px-2 py-1.5 border rounded text-sm focus:outline-none ${
            isNegative ? 'border-warning-border bg-warning-bg text-warning-text focus:border-warning-text' : 'border-neutral-line focus-ring'
          }`}
        />
        {isNegative && (
          <div className="text-[10px] text-warning-text mt-0.5">マイナス金額（返金・値引き等）</div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-h1 flex items-center gap-2">
          <Icon size={26} className="text-brand-red" strokeWidth={1.75} />
          {config.title}
        </h1>
        <p className="text-sm text-neutral-sub mt-1">{config.description}</p>
      </div>

      {/* 店舗・対象月選択 */}
      <div className="border border-neutral-line rounded-lg bg-white p-4 flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="me-store" className="block text-xs text-neutral-sub mb-1">店舗 *</label>
          <select
            id="me-store"
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            className="min-w-[180px] px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
          >
            <option value="">選択してください</option>
            {stores.map((s) => (
              <option key={s.store_id} value={s.store_id}>
                {s.store_name}{s.status === 'closed' ? '（停止中）' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="me-month" className="block text-xs text-neutral-sub mb-1">対象月 *</label>
          <MonthPicker
            id="me-month"
            aria-label="対象月 (必須)"
            value={month}
            onChange={setMonth}
            className="px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
          />
        </div>
        {storeId && month && (
          <div className="text-xs text-neutral-sub flex items-center gap-1">
            <Info size={14} />
            {existingQuery.isLoading || existingQuery.isFetching
              ? '既存データを確認中…'
              : `この店舗・月には現在 ${existingQuery.data?.count ?? 0} 件のデータがあります（下表に読込済み。保存すると下表に無い行は削除されます）`}
          </div>
        )}
      </div>

      {storeId && month && (
        <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-4">
          {config.singleRow ? (
            // 単一行フォーム (人件費: 店舗×月につき1件)
            <div className="flex flex-wrap gap-4">
              {config.fields.map((f) => (
                <div key={f.name} className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-neutral-sub mb-1">
                    {f.label}{f.required && <span className="text-warning-text ml-0.5">*</span>}
                  </label>
                  {renderField(rows[0] ?? emptyRow(config.fields), 0, f)}
                </div>
              ))}
            </div>
          ) : (
            // 複数行テーブル (諸経費・デリバリー売上)
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-panel text-neutral-sub">
                  <tr>
                    <th className="w-8"></th>
                    {config.fields.map((f) => (
                      <th key={f.name} className="text-left px-2 py-2 whitespace-nowrap">
                        {f.label}{f.required && <span className="text-warning-text ml-0.5">*</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={config.fields.length + 1} className="text-center text-neutral-sub py-6">
                        「行を追加」ボタンで入力を開始してください
                      </td>
                    </tr>
                  )}
                  {rows.map((row, idx) => (
                    <tr key={idx} className="border-t border-neutral-line">
                      <td className="px-1">
                        <button
                          type="button"
                          onClick={() => removeRow(idx)}
                          className="text-neutral-sub hover:text-warning-text p-1"
                          aria-label="この行を削除"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                      {config.fields.map((f) => (
                        <td key={f.name} className="px-1 py-1">{renderField(row, idx, f)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                onClick={addRow}
                className="mt-3 flex items-center gap-1 text-sm text-brand-red hover:underline"
              >
                <Plus size={14} /> 行を追加
              </button>
            </div>
          )}

          {errors && errors.length > 0 && (
            <div className="p-3 rounded bg-warning-bg border border-warning-border">
              <div className="flex items-start gap-2 text-sm text-warning-text font-semibold mb-1">
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                入力内容にエラーがあります
              </div>
              <ul className="text-xs text-neutral-text space-y-0.5 pl-6 list-disc">
                {errors.slice(0, 20).map((e, i) => (
                  <li key={i}>{e.column ? `${e.column}: ` : ''}{e.message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end items-center gap-2 pt-2 border-t border-neutral-line">
            {storeId && month && <InputReadyButton nodeId={storeId} targetMonth={month} dataType={config.dataType} />}
            <button
              type="button"
              onClick={openConfirm}
              disabled={saving || !dataReady}
              title={!dataReady ? '既存データの読込中は保存できません' : undefined}
              className="px-5 py-2 rounded bg-brand-red text-white text-sm font-medium hover:bg-brand-red-hover disabled:opacity-50"
            >
              {saving ? '保存中…' : '保存する'}
            </button>
          </div>
        </div>
      )}

      {(!storeId || !month) && (
        <div className="p-8 text-center text-neutral-sub border border-neutral-line rounded-lg bg-white">
          店舗と対象月を選択すると入力欄が表示されます
        </div>
      )}

      {/* 全置換方式であることを保存前に明示 (Design レビュー CRITICAL 対応):
          「表示されている行だけが保存後に残る」という破壊的な意味を、件数の差分とともに確認させる */}
      <ConfirmDialog
        open={confirmOpen}
        title={`${config.title.replace('手入力', '').trim()}を保存しますか？`}
        destructive={Boolean(existingQuery.data && filledRows.length < existingQuery.data.count)}
        confirmLabel="保存する"
        loading={saving}
        onClose={() => { if (!saving) setConfirmOpen(false); }}
        onConfirm={doSave}
        message={
          <div className="space-y-2">
            <p>
              {selectedStore?.store_name} の {month} 分として、<strong>{filledRows.length} 件</strong>を保存します。
            </p>
            <p className="text-xs text-neutral-sub">
              この操作はこの店舗・月の既存データを全て置き換えます（現在 {existingQuery.data?.count ?? 0} 件 → 保存後 {filledRows.length} 件）。
              下の入力欄に表示されていない行は削除されます。
            </p>
            {existingQuery.data && filledRows.length < existingQuery.data.count && (
              <div className="p-2 rounded bg-warning-bg border border-warning-border text-xs text-warning-text flex items-start gap-1.5">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                {existingQuery.data.count - filledRows.length} 件が削除されます。よろしいですか？
              </div>
            )}
            {negativeRowCount > 0 && (
              <div className="p-2 rounded bg-warning-bg border border-warning-border text-xs text-warning-text flex items-start gap-1.5">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                {negativeRowCount} 件にマイナスの金額が含まれています。返金・値引き等の意図的な入力でなければ内容をご確認ください。
                （マイナス行は既存のプラス行とは別レコードとして保存され、集計時に自動的に相殺されます）
              </div>
            )}
          </div>
        }
      />
    </div>
  );
}
